/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import { format } from 'date-fns';
import mbpLogger from '@module-logger/mbp';

import availablityDeliveryCalendar from '../../../../../apis/cart-service-apis/availablityDeliveryCalendar';
import { getAccessTokenSafely } from '../../../../../state/ducks/Member/ducks/Auth/Plugin/auth/auth';
import getLocationType from '../../../../helpers/getLocationType';
import { isEmpty } from '../../utils/object';
import pastEndDate from '../Subscriptions/Partials/CommonUtils';

const getStatusCSS = (subscription) => {
    let subscriptionStatus;
    let subscriptionStatusClass;
    const updateStatusForExpiredProduct = () => {
        const pastDate = pastEndDate(subscription);
        if (pastDate) {
            subscriptionStatus = 'COMPLETED';
            subscriptionStatusClass = 'activeStatus';
        }
    };

    switch (subscription.subscriptionStatus) {
        case 'PROCESSED':
            subscriptionStatus = 'ACTIVE';
            subscriptionStatusClass = 'activeStatus';
            updateStatusForExpiredProduct();
            break;
        case 'UPDATED':
            subscriptionStatus = 'UPDATED';
            subscriptionStatusClass = 'activeStatus';
            updateStatusForExpiredProduct();
            break;
        case 'CANCELLED':
            subscriptionStatus = 'CANCELLED';
            subscriptionStatusClass = 'canceledStatus';
            break;
        case 'SKIPPED':
            subscriptionStatus = 'SKIPPED';
            subscriptionStatusClass = 'pausedStatus';
            updateStatusForExpiredProduct();
            break;
        case 'RESUMED':
            subscriptionStatus = 'RESUMED';
            subscriptionStatusClass = 'pausedStatus';
            updateStatusForExpiredProduct();
            break;
        default:
            subscriptionStatus = 'ACTIVE';
            subscriptionStatusClass = 'activeStatus';
            updateStatusForExpiredProduct();
    }
    return { subscriptionStatus, subscriptionStatusClass };
};

export default getStatusCSS;

export const enableEditButton = (subscription) => {
    if (pastEndDate(subscription)) return false;
    if (subscription.subscriptionStatus === 'CANCELLED') return false;
    if (subscription?.subscriptionType?.toLowerCase() === 'monthonlypayfull') return false;
    return true;
};

export const removeUSZipCodeSegment = (zipcode) => {
    if (/\d{5}-\d{4}/.test(zipcode)) {
        const zipSplit = zipcode.split('-');
        return zipSplit[0];
    }
    return zipcode;
};

export const giftText = (message) => {
    let currentMessage = message || '';
    let signatureMessage = '';
    const regexPattern = /\s*(?:FROM:\s\|{3}|FROM:|From:|\s-\s)\s*/g;

    const matches = Array.from(currentMessage.matchAll(regexPattern));

    if (matches.length > 0) {
        const lastMatch = matches[matches.length - 1];
        signatureMessage = currentMessage.slice(lastMatch.index + lastMatch[0].length);
        currentMessage = currentMessage.slice(0, lastMatch.index);
    }

    return [currentMessage, signatureMessage];
};

export const sortSubscriptions = (subscriptions, activeFilter) => {
    if (isEmpty(subscriptions)) return [];

    const activeSubscription = subscriptions.filter((subscription) => ['PROCESSED'].includes(subscription.subscriptionStatus) && !pastEndDate(subscription));
    const skippedSubscription = subscriptions.filter((subscription) => ['SKIPPED'].includes(subscription.subscriptionStatus) && !pastEndDate(subscription));
    const updatedSubscription = subscriptions.filter((subscription) => ['UPDATED'].includes(subscription.subscriptionStatus) && !pastEndDate(subscription));
    const resumedSubscription = subscriptions.filter((subscription) => ['RESUMED'].includes(subscription.subscriptionStatus) && !pastEndDate(subscription));
    const failedClubSubscription = subscriptions.filter((subscription) => ['CLUB_COMPONENT_FAILED'].includes(subscription.subscriptionStatus) && !pastEndDate(subscription));
    if (activeFilter === 'ACTIVE') return [...activeSubscription, ...updatedSubscription, ...resumedSubscription, ...skippedSubscription, ...failedClubSubscription];

    const pausedSubscription = subscriptions.filter((subscription) => ['PAUSED'].includes(subscription.subscriptionStatus) && !pastEndDate(subscription));
    if (activeFilter === 'PAUSED') return pausedSubscription;

    const cancelledSubscription = subscriptions.filter((subscription) => ['CANCELLED'].includes(subscription.subscriptionStatus) || pastEndDate(subscription));
    if (activeFilter === 'CANCELLED') return cancelledSubscription;

    return [...activeSubscription, ...updatedSubscription, ...resumedSubscription, ...skippedSubscription, ...pausedSubscription, ...cancelledSubscription];
};

export const getSubscriptionStatuses = (filter) => (filter === 'ACTIVE' ? ['PROCESSED'] : [filter]);

export const scrollToId = ({ id, top }) => {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        if (!id) {
            window.scrollTo({ top: top || 0, behavior: 'smooth' });
            return;
        }
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        }
    }
};

export const limitTextWithWordBoundary = (text, limit) => {
    if (text.length <= limit) {
        return text;
    }

    const substring = text.substring(0, limit);
    const lastSpaceIndex = substring.lastIndexOf(' ');

    if (lastSpaceIndex !== -1) {
        // Found a space within the character limit, truncate at the space.
        return `${substring.substring(0, lastSpaceIndex)}...`;
    }
    // No space found within the character limit, just truncate at the limit.
    return `${substring}...`;
};

export const getPriceDiffLabel = (productPrice, currentSkuPrice) => {
    if (productPrice > currentSkuPrice) return `-$${parseFloat(productPrice - currentSkuPrice).toFixed(2)}`;
    if (productPrice < currentSkuPrice) return `+$${parseFloat(currentSkuPrice - productPrice).toFixed(2)}`;
    return 'Same price!';
};

export const brandByCode = {
    1001: '1800flowers',
    1019: 'harryanddavid',
    1009: '1800baskets',
    1025: 'simplychocolate',
    1010: 'fruitbouquets',
    1012: 'cheryls',
    1022: 'personalizationuniverse',
    1002: 'thepopcornfactory',
    1020: 'wolfermans',
    1018: 'stockyards',
    1030: 'personalizationmall',
    1031: 'vitalchoice',
    1033: 'thingsremembered',
    1029: 'berries',
};

export const productAvailability = async (subscription, productCode, zipCode, membershipType, setIsProductAvailable, setCheckProductAvailability) => {
    const jwt = await getAccessTokenSafely();
    const locationType = subscription?.recipient?.[0]?.locationType;
    let res = false;
    await availablityDeliveryCalendar({
        env: {},
        jwtToken: jwt,
        brandCode: subscription.brand,
        productCode,
        locationType: getLocationType(locationType),
        zipCode,
        startDate: format(new Date(subscription.nextOrderDate), 'dd-MMM-yy'),
        variantId: null,
        customerType: membershipType,
    })
        .then(({ data }) => {
            if (!data?.response?.calendarDates?.length) {
                setIsProductAvailable('not_available');
                return;
            }
            res = true;
            setIsProductAvailable('available');
        })
        .catch((err) => {
            setIsProductAvailable('not_available');
            res = false;
            mbpLogger.logError({
                appName: process.env.npm_package_name,
                component: 'Recommended Subscription Variants',
                apiCall: 'Availability DeliveryCalendar - cart services',
                error: err,
            });
        });
    setCheckProductAvailability(false);
    return res;
};
